<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card ref="rootCard" class="fill-height">
			<Toolbar :items="toolbarItems" :panel-params="params" />
			<Toolbar v-if="selectedStation" :items="headerItems" />
			<NoStationSelectedContainer v-if="!selectedStation" />
			<v-card class="fill-height" rounded="t-0">
				<draggable draggable=".draggable" :group="{name: 'now-playing', put: true, pull: false}" class="h-100">
					<v-card elevation="1" class="fill-height">
						<v-container v-if="sequencer && selectedStation" class="pa-2" fluid>
							<OnAirEvent
								v-for="event in itemsToDisplay"
								:key="event.EventID"
								:selected-station="selectedStation"
								:event="event"
								:sequencer-items="sequencerItems"
								:list-chain-stop="filterChainStop"
							/>
						</v-container>
					</v-card>
				</draggable>
				<Loader :value="!sequencer" />
			</v-card>
			<SelectSplitMasterStationDialog
				v-if="selectSplitMasterStationDialog.open"
				:dialog="selectSplitMasterStationDialog"
				:options="selectSplitMasterStationDialog.options"
				@close="selectSplitMasterStationDialog.open = false"
			/>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import {VueDraggableNext} from "vue-draggable-next";
import emitter from "@/emitter";
import moment from "moment-timezone";

/* Modules */
import {VBtn, VBtnToggle, VIcon, VTooltip} from "vuetify/components";
import Image from "@/components/Image.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import OnAirEvent from "@/components/OnAirEvent.vue";
import SelectSplitMasterStationDialog from "@/components/dialogs/SelectSplitMasterStationDialog.vue";
import StationDateTime from "@/components/StationDateTime.vue";
import Toolbar from "@/components/Toolbar.vue";

/* Enums */
import ChainTypes from "@/enums/ChainTypes";
import SequencerStatus from "@/enums/SequencerStatus";
import SequencerModes from "@/enums/SequencerModes";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import SyndicationTypes from "@/enums/SyndicationTypes";

/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import PanelPropertiesMixin from "@/mixins/PanelProperties";

const PANEL_MIN_WIDTH = 830;

export default {
	name: "OnAir",

	components: {
		draggable: VueDraggableNext,
		Loader,
		NoStationSelectedContainer,
		OnAirEvent,
		SelectSplitMasterStationDialog,
		StationDateTime,
		Toolbar
	},

	mixins: [DurationFormatMixin, PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			containerHeight: 0,
			audioElement: null,
			analyser: null,
			isPlaying: false,
			selectSplitMasterStationDialog: {
				open: false,
				options: {}
			}
		};
	},

	computed: {
		toolbarItems() {
			if (!this.selectedStation) {
				return [];
			}

			return [
				{
					...(this.sequencerStatus && {
						component: VTooltip,
						props: {
							text: "Sequencer Status",
							location: "bottom",
							openDelay: 500
						},
						slots: [
							{
								name: "activator",
								items: data => [
									{
										component: "div",
										props: data.props,
										slots: [
											{
												name: "default",
												items: [
													{
														component: VBtn,
														props: {
															text:
																SequencerStatus.getByValue(this.sequencerStatus)
																	?.title ?? "",
															variant: "text",
															height: 40,
															readonly: true,
															style: {
																lineHeight: "20px"
															}
														},
														slots: [
															{
																name: "prepend",
																items: [
																	{
																		component: VIcon,
																		props: {
																			icon: SequencerStatus.getByValue(
																				this.sequencerStatus
																			)?.icon,
																			size: "medium",
																			color: SequencerStatus.getByValue(
																				this.sequencerStatus
																			)?.color
																		}
																	}
																]
															}
														]
													}
												]
											}
										]
									}
								]
							}
						]
					})
				},
				{
					component: VTooltip,
					props: {
						text: "Click to toggle through modes",
						location: "bottom",
						disabled: !this.sequencerMode || this.pendingChangeMode,
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: VBtnToggle,
									props: {
										...data.props,
										modelValue: this.sequencerMode,
										class: {
											"mode-btn-group": true,
											"d-none": !this.availableModes?.length,
											"ml-1": true,
											"ml-xl-3": !this.lgAndDown
										},
										density: "comfortable",
										disabled: !this.sequencerMode || this.pendingChangeMode
									},
									slots: [
										{
											name: "default",
											items: [
												{
													component: VBtn,
													props: {
														text: "Live Assist",
														value: SequencerModes.LiveAssist,
														class: {
															"mode-btn": true,
															live_assist: true,
															pending: this.pendingChangeMode
														},
														height: 40
													}
												},
												{
													component: VBtn,
													props: {
														text: "Auto",
														value: SequencerModes.Auto,
														class: {
															"mode-btn": true,
															auto: true,
															pending: this.pendingChangeMode
														},
														height: 40
													}
												},
												{
													component: VBtn,
													props: {
														text: "Split",
														value: SequencerModes.Split,
														class: {
															"mode-btn": true,
															split: true,
															pending: this.pendingChangeMode
														},
														height: 40
													}
												},
												{
													component: VBtn,
													props: {
														text: "Manual",
														value: SequencerModes.Manual,
														class: {
															"mode-btn": true,
															manual: true,
															pending: this.pendingChangeMode
														},
														height: 40
													}
												}
											].filter(
												button =>
													this.availableModes?.includes(button.props.value) &&
													(button.props.value !== SequencerModes.Split ||
														this.selectedStation?.SyndicationType !==
															SyndicationTypes.SplitMaster) // for the Split mode, ensure if the station's syndication type is not Split master
											) // filter buttons that are available based on station settings
										}
									],
									actions: {
										"update:modelValue": this.setSequencerMode
									}
								}
							]
						}
					]
				},
				{
					component: VBtn,
					props: {
						text: "Listen",
						class: "ml-1",
						variant: "tonal",
						height: 40
					},
					slots: [
						{
							name: "prepend",
							items: [
								{
									component: VIcon,
									props: {
										icon: "mdi-volume-high"
									}
								}
							]
						},
						{
							...(this.isPlaying && {
								name: "append",
								items: [
									{
										component: "canvas",
										props: {
											id: "canvas",
											width: "10",
											height: "30",
											style: {
												border: "1px solid #c8cdd0",
												"border-radius": "3px"
											}
										}
									}
								]
							})
						}
					],
					actions: {
						click: this.toggleStreamURL
					}
				}
			];
		},

		headerItems() {
			if (!this.selectedStation) {
				return [];
			}
			return [
				{
					component: "div",
					props: {
						style: {width: "180px"}
					},
					slots: [
						{
							name: "default",
							items: [
								{
									component: StationDateTime,
									props: {
										dateTime: this.stationDateTime,
										format: "dddd",
										className: "text-h3"
									}
								},
								{
									component: StationDateTime,
									props: {
										dateTime: this.stationDateTime,
										format: "MMMM DD, YYYY",
										className: "text-h3"
									}
								}
							]
						}
					]
				},
				{
					component: StationDateTime,
					props: {
						dateTime: this.stationDateTime,
						format: "hh:mm:ss A",
						autoUpdate: true,
						class: "text-h1 ml-2",
						style: {width: "180px"}
					}
				},
				{
					component: "div",
					props: {
						class: "text-h3 ml-2",
						style: {width: "120px"}
					},
					slots: [
						{
							name: "default",
							items: [
								{
									component: "div",
									text: this.gapTimeFormatted
								},
								{
									...((this.gapTime || this.gapTimeTargetDateTime) && {
										component: "div",
										text: this.gapTimeStart
									})
								}
							]
						}
					]
				},
				// PAUSE button
				{
					component: VTooltip,
					props: {
						text: this.formatterTextToShow("Pause", this.sequencerMode === SequencerModes.Auto),
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: "div",
									props: data.props,
									slots: [
										{
											name: "default",
											items: [
												{
													component: VBtn,
													outerClass: "ml-auto",
													props: {
														...data.props,
														...(this.$vuetify.display.xxl && {text: "PAUSE"}),
														...(this.$vuetify.display.xlAndDown && {
															icon: "mdi-pause-circle"
														}),
														class: "mr-2 font-weight-bold v-btn--has-bg",
														height: 40,
														width: this.$vuetify.display.xxl ? 100 : 40,
														elevation: 1,
														style: {
															"border-radius": this.$vuetify.display.xxl ? "6px" : "3px"
														},
														disabled: this.pauseAndStopButtonsDisabled
													},
													actions: {
														click: () => {
															this.sequencerAction({
																action: "Pause",
																msg: {EventID: null}
															});
														}
													},
													slots: [
														{
															...(this.$vuetify.display.xxl && {
																name: "prepend",
																items: [
																	{
																		component: VIcon,
																		props: {
																			icon: "mdi-pause-circle",
																			size: 24
																		}
																	}
																]
															})
														}
													]
												}
											]
										}
									]
								}
							]
						}
					]
				},
				// STOP button
				{
					component: VTooltip,
					props: {
						text: this.formatterTextToShow("Stop (F10)", this.sequencerMode === SequencerModes.Auto),
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: "div",
									props: data.props,
									slots: [
										{
											name: "default",
											items: [
												{
													component: VBtn,
													props: {
														...(this.$vuetify.display.xxl && {text: "STOP"}),
														...(this.$vuetify.display.xlAndDown && {icon: "mdi-stop"}),
														class: "mr-2 font-weight-bold v-btn--has-bg",
														height: 40,
														width: this.$vuetify.display.xxl ? 100 : 40,
														elevation: 1,
														style: {
															"border-radius": this.$vuetify.display.xxl ? "6px" : "3px"
														},
														disabled: this.pauseAndStopButtonsDisabled
													},
													actions: {
														click: () => {
															this.sequencerAction({
																action: "Stop",
																msg: {EventID: null}
															});
														}
													},
													slots: [
														{
															...(this.$vuetify.display.xxl && {
																name: "prepend",
																items: [
																	{
																		component: VIcon,
																		props: {
																			icon: "mdi-stop",
																			size: 24
																		}
																	}
																]
															})
														}
													]
												}
											]
										}
									]
								}
							]
						}
					]
				},
				// END button
				{
					component: VTooltip,
					props: {
						text: this.formatterTextToShow("End", this.sequencerMode !== SequencerModes.LiveAssist),
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: "div",
									props: data.props,
									slots: [
										{
											name: "default",
											items: [
												{
													component: VBtn,
													props: {
														...(this.$vuetify.display.xlAndUp && {text: "END"}),
														class: "mr-2 font-weight-bold v-btn--has-bg",
														icon: !this.$vuetify.display.xlAndUp,
														height: 40,
														width: this.$vuetify.display.xlAndUp ? 100 : 40,
														elevation: 1,
														style: {
															...(this.isNowPlayingEventChainStop && {
																"background-color": "rgba(198, 0, 43, 0.2) !important"
															}),
															"border-radius": this.$vuetify.display.xlAndUp
																? "6px"
																: "3px"
														},
														disabled: this.endButtonDisabled
													},
													actions: {
														click: () => {
															this.sequencerAction({action: "End"});
														}
													},
													slots: [
														this.$vuetify.display.xlAndUp
															? {
																	name: "prepend",
																	items: [
																		{
																			component: Image,
																			props: {
																				img: "End_icon.svg",
																				width: "20",
																				height: "20"
																			}
																		}
																	]
																}
															: {
																	name: "default",
																	items: [
																		{
																			component: Image,
																			props: {
																				img: "End_icon.svg",
																				width: "20",
																				height: "20"
																			}
																		}
																	]
																}
													]
												}
											]
										}
									]
								}
							]
						}
					]
				},
				// PLAY button
				{
					component: VTooltip,
					props: {
						text: "Play (F9)",
						location: "bottom",
						openDelay: 1000
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: "div",
									props: data.props,
									slots: [
										{
											name: "default",
											items: [
												{
													component: VBtn,
													props: {
														...(this.$vuetify.display.lgAndUp && {text: "PLAY"}),
														...(!this.$vuetify.display.lgAndUp && {
															icon: "mdi-play-circle"
														}),
														class: "font-weight-bold v-btn--has-bg",
														height: 40,
														width: this.$vuetify.display.lgAndUp ? 100 : 40,
														elevation: 1,
														color: "white",
														style: {
															"background-color": `${
																!this.darkMode ? "#62AA7B" : "#3DB868"
															} !important`,
															"border-radius": this.$vuetify.display.lgAndUp
																? "6px"
																: "3px"
														},
														disabled:
															this.sequencerActionStatus.pending ||
															this.sequencerMode === SequencerModes.Split
													},
													actions: {
														click: () => {
															this.sequencerAction({
																action: "PlayNext",
																msg: {EventID: null}
															});
														}
													},
													slots: [
														{
															...(this.$vuetify.display.lgAndUp && {
																name: "prepend",
																items: [
																	{
																		component: VIcon,
																		props: {
																			icon: "mdi-play-circle",
																			size: 24
																		}
																	}
																]
															})
														}
													]
												}
											]
										}
									]
								}
							]
						}
					]
				}
			];
		},

		sequencer() {
			return this.$store.getters.getSequencer(this.selectedStationID);
		},

		sequencerItems() {
			return this.$store.getters.getSequencerItems(this.selectedStationID);
		},

		itemsToDisplay() {
			const numberOfItems = Math.floor(this.containerHeight / 50); // 50px is the height of each item
			return this.$store.getters.getSequencerItems(this.selectedStationID).slice(0, numberOfItems);
		},

		allNowPlayingEvents() {
			let currentItems = _.filter(
				this.$store.getters.getSequencerItems(this.selectedStationID),
				e => e.StatusCode === EventStatus.Current
			);

			currentItems =
				currentItems.length > 0
					? currentItems
					: _.filter(this.playlistsEvents, e => e.StatusCode === EventStatus.Current);

			return currentItems.reduce((array, item) => {
				if ([EventTypes.SpotBlock, EventTypes.LocalBreak].includes(item.EventType)) {
					const spots = item?.ChildItems ?? item.ChildPlaylist?.Events ?? []; //if spotblock or Local Break comes from Sequencer's items or playlist's events
					array.push(...spots.filter(c => c.StatusCode === EventStatus.Current));
				} else {
					array.push(item);
				}
				return array;
			}, []);
		},

		nowPlayingEvent() {
			return _.findLast(this.sequencerItems, e => e.StatusCode === EventStatus.Current);
		},

		isNowPlayingEventChainStop() {
			return this.nowPlayingEvent?.Chain === ChainTypes.Stop && this.sequencerMode === SequencerModes.LiveAssist;
		},

		sequencerStatus() {
			return this.$store.getters.getSequencerStatus(this.selectedStationID);
		},

		sequencerMode() {
			return this.$store.getters.getSequencerMode(this.selectedStationID);
		},

		availableModes() {
			return this.selectedStation?.AvailableModes;
		},

		pendingChangeMode() {
			return this.$store.getters.sequencerModePending(this.selectedStationID);
		},

		pauseAndStopButtonsDisabled() {
			return (
				!this.nowPlayingEvent ||
				this.sequencerActionStatus.pending ||
				this.sequencerMode === SequencerModes.Auto ||
				this.sequencerMode === SequencerModes.Split ||
				(["Pause", "Stop"].includes(this.waitingSequencerResponse.action)
					? this.waitingSequencerResponse.state
					: false)
			);
		},

		endButtonDisabled() {
			return (
				!this.nowPlayingEvent ||
				this.sequencerActionStatus.pending ||
				this.sequencerMode !== SequencerModes.LiveAssist ||
				(this.waitingSequencerResponse.action === "End" ? this.waitingSequencerResponse.state : false)
			);
		},

		sequencerActionStatus() {
			return this.$store.getters.sequencerActionPending(this.selectedStationID);
		},

		filterChainStop() {
			return this.sequencerItems.filter(item => item.Chain === ChainTypes.Stop);
		},

		etmWithGapTime() {
			//We do not use the "sequencerItems" computed variable, since we filter out the ETM events there
			return this.sequencer?.Items?.find(e => e.EventType === EventTypes.ETM && e.GapTime);
		},

		gapTime() {
			return this.etmWithGapTime?.GapTime ?? this.sequencer?.GapTime ?? 0;
		},

		gapTimeTargetDateTime() {
			return this.sequencer?.GapTimeTargetDateTime;
		},

		gapTimeFormatted() {
			return `Gap: ${this.gapTime > 0 ? "+" : ""}${this.secondsToFormat(this.gapTime, "hh:mm:ss", {
				trim: "left",
				stopTrim: "m"
			})}`;
		},

		gapTimeStart() {
			const start = this.etmWithGapTime?.StartDateTime ?? this.sequencer?.GapTimeTargetDateTime;
			return start && moment.tz(start, this.stationTimezone).format("hh:mm:ss A");
		},

		waitingSequencerResponse() {
			return this.$store.getters.getSequencerSnapshotUpdated(this.selectedStationID);
		},

		darkMode() {
			return this.$store.getters.darkMode();
		},

		lgAndDown() {
			return this.$vuetify.display.lgAndDown;
		},

		clientId() {
			return this.$store.getters.clientId();
		}
	},

	watch: {
		selectedStationID: {
			handler(stationID, previousStationID) {
				if (!this.liveMode) {
					if (previousStationID) {
						this.$store.dispatch("clearSequencerSnapshotExtendRequestInterval", previousStationID);
						this.$store.dispatch("sequencerSnapshotSubscription", {
							stationID: previousStationID,
							subscribe: false
						});
						this.$store.commit("removeSequencer", previousStationID);
					}

					if (stationID && stationID !== previousStationID) {
						this.$store.dispatch("sequencerSnapshotSubscription", {stationID});
						this.$store.dispatch("startSequencerSnapshotExtendRequestInterval", stationID);
						this.$store.dispatch("sequencerSnapshotRequest", stationID);
					}
				}
				if (stationID && stationID !== previousStationID) {
					this.setupPlayer();
				}
			},
			immediate: true
		}
	},

	created() {
		document.addEventListener("keydown", this.handleKeyboardEvents);
	},

	mounted() {
		// Constraints belong to the group, so reapply them whenever the panel is moved into another one.
		this.updateMinWidth();
		this.groupChangeDisposable = this.params?.api?.onDidGroupChange(this.updateMinWidth);

		// Use ResizeObserver to track container height and compute usable space
		this.$nextTick(() => {
			this.resizeObserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					this.containerHeight = entry.target.offsetHeight - 116; // Subtract fixed UI elements (header + toolbar + padding = 116px)
				}
			});
			const el = this.$refs.rootCard?.$el;
			if (el instanceof Element) {
				this.resizeObserver.observe(el);
				// Initialize immediately
				this.containerHeight = el.offsetHeight;
			}
		});

		// Both the hardware MIDI controller (Controller.js) and the on-screen MIDI panel
		// buttons (TopButton/BottomButton, via MidiUI) route sequencer control through these
		// same two emitter events, so this is the single place that turns them into actions.
		emitter.on("midi:setSequencerAction", this.sequencerAction);
		emitter.on("midi:setSequencerMode", this.setSequencerMode);
	},

	beforeUnmount() {
		this.$store.commit("removeSequencer", this.selectedStationID);

		document.removeEventListener("keydown", this.handleKeyboardEvents);
		emitter.off("midi:setSequencerAction", this.sequencerAction);
		emitter.off("midi:setSequencerMode", this.setSequencerMode);

		this.groupChangeDisposable?.dispose();

		if (this.resizeObserver) {
			const el = this.$refs.rootCard?.$el;
			if (el instanceof Element) {
				this.resizeObserver.unobserve(el);
			}
		}
	},

	methods: {
		setSequencerMode(mode) {
			if (mode === SequencerModes.Split) {
				const selectStationOnSplitModeFeatureIsEnabled = this.$store.getters.isFeatureEnabled({
					object_id: this.selectedStation.StationID,
					reference: "zettacloud_live_console_on_split_mode_select_station"
				});
				if (selectStationOnSplitModeFeatureIsEnabled) {
					this.selectSplitMasterStationDialog = {
						open: true,
						options: {
							station: this.selectedStation,
							confirmAction: () =>
								this.$store.dispatch("sequencerModeRequest", {stationID: this.selectedStationID, mode})
						}
					};
					return;
				}
			}
			if (mode === SequencerModes.Auto && this.nowPlayingEvent?.Chain === ChainTypes.Stop) {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "Switching to Auto Mode will cancel current track’s STOP SONG chain type."
				});
			}
			this.$store.dispatch("sequencerModeRequest", {stationID: this.selectedStationID, mode});
		},

		setupPlayer() {
			this.audioElement = new Audio(this.getListenURL());
			this.audioElement.crossOrigin = "anonymous";
			const audioContext = new (window.AudioContext || window.webkitAudioContext)();
			const source = audioContext.createMediaElementSource(this.audioElement);
			this.analyser = audioContext.createAnalyser();
			source.connect(this.analyser);
			this.analyser.connect(audioContext.destination);
		},

		getListenURL() {
			let listenURL = null;
			const primaryPlayer = _.values(this.selectedStation?.PlayerDictionary)?.find(
				player => player.PlayerEnabled && player.PlayerPurposeType === 1
			); //Purpose type Primary

			if (primaryPlayer) {
				const mixer = primaryPlayer.MixerDictionary?.Default_Mixer;
				const output = mixer?.OutputDictionary?.Default_Output;
				listenURL = output?.OutputLiveStreamUrl;
			}

			return listenURL;
		},

		toggleStreamURL() {
			if (this.isPlaying) {
				this.audioElement.pause();
				this.isPlaying = false;
				return;
			}

			if (this.audioElement.src) {
				this.audioElement.play().then(() => {
					this.isPlaying = true;

					this.$nextTick(this.drawVolume);
				});
			}
		},

		drawVolume() {
			const canvas = document.getElementById("canvas");
			const canvasCtx = canvas.getContext("2d");
			const bufferLength = this.analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const draw = () => {
				this.analyser.getByteFrequencyData(dataArray);
				const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
				const barWidth = canvas.width;
				const barHeight = (average / 128) * canvas.height;

				const gradient = canvasCtx.createLinearGradient(0, 0, 0, canvas.height);
				gradient.addColorStop(0, "red"); // Top
				gradient.addColorStop(0.25, "yellow");
				gradient.addColorStop(1, "lime"); // Bottom

				// Clear and draw vertical bar
				canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
				canvasCtx.fillStyle = gradient;
				canvasCtx.fillRect(0, canvas.height - barHeight, barWidth, barHeight);

				requestAnimationFrame(draw);
			};
			draw();
		},

		async sequencerAction(button) {
			if (button.action !== "PlayNext") {
				this.$store.commit("setSequencerSnapshotUpdated", {
					stationID: this.selectedStationID,
					status: {action: button.action, state: true}
				});
			} else {
				const nextSequencerItem = this.$store.getters
					.getSequencerItems(this.selectedStationID)
					.find(e => e.StatusCode === EventStatus.Ready);

				if (nextSequencerItem.AssetID) {
					const asset = await this.$store.dispatch("getAsset", nextSequencerItem.AssetID);
					const isSyndicatedShowEvent = this.isValidURL(asset?.POIMarkup?.Path);
					const isFutureSyndicatedShowEvent =
						isSyndicatedShowEvent && moment().isBefore(asset.ReleaseDateTime);

					if (isFutureSyndicatedShowEvent) {
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "The item you are attempting to play next is scheduled to start in the future."
						});
						return;
					}
				}
			}
			if (button.action === "End") {
				let stopChainEvents = this.allNowPlayingEvents.filter(e => e.Chain === ChainTypes.Stop);
				if (stopChainEvents.length > 0) {
					stopChainEvents.forEach(e => {
						const {EventID, Chain} = e;
						this.$store.dispatch("sequencerEndActionRequest", {
							stationID: this.selectedStationID,
							EventID,
							Chain
						});
					});
				} else {
					const {EventID, Chain} = _.findLast(this.allNowPlayingEvents);
					this.$store.dispatch("sequencerEndActionRequest", {
						stationID: this.selectedStationID,
						EventID,
						Chain
					});
				}
			} else {
				const {action, msg} = button;
				this.$store.dispatch("sequencerActionRequest", {stationID: this.selectedStationID, msg, action});
			}
		},

		isValidURL(url) {
			try {
				return Boolean(new URL(url));
			} catch (e) {
				return false;
			}
		},

		formatterTextToShow(nameButton, sequencerModeValidation) {
			if (!this.nowPlayingEvent) {
				return `${nameButton} is inactive when there is no audio playing.`;
			}

			if (sequencerModeValidation) {
				const sequencerModeName = SequencerModes.getName(this.sequencerMode)
					.split(/(?<=[a-z])(?=[A-Z])/)
					.join(" ");
				return `${nameButton} is disabled in ${sequencerModeName} .`;
			}

			return `${nameButton}`;
		},

		handleKeyboardEvents(ev) {
			if (ev.key === "F10") {
				// STOP the next playlist asset/element from playing
				ev.preventDefault();
				if (this.pauseAndStopButtonsDisabled) {
					return;
				}
				this.sequencerAction({action: "Stop", msg: {EventID: null}});
			} else if (ev.key === "F9") {
				// PLAY the next element in the Playlist
				ev.preventDefault();
				if (this.sequencerActionStatus.pending) {
					return;
				}
				this.sequencerAction({action: "PlayNext", msg: {EventID: null}});
			}
		},

		updateMinWidth() {
			this.params?.api?.group?.api?.setConstraints({minimumWidth: PANEL_MIN_WIDTH});
		}
	}
};
</script>
